import { useEffect } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useBootstrap from '../../hooks/useBootstrap';
import { fetchCatalog } from '../../core/queries/catalog';
import { fetchGalleryPage, GALLERY_PAGE_SIZE } from '../../core/queries/gallery';
import { queryClient } from '../../core/queryClient';
import { queryKeys } from '../../core/queries/queryKeys';
import { findGroupInBootstrap, seedPresetGroup } from '../../core/queries/presetGroups';
import HeroCarousel from './HeroCarousel';
import HomeSection from './HomeSection';
import HomeSkeleton from './HomeSkeleton';
import { useAuthStore } from '../../store/authStore';
import { hapticSelection } from '../../utils/haptics';
import { useConsentStore } from '../../store/consentStore';

/**
 * HomePage — композиция стадии 02 (figma 318:3123).
 *
 * Состояния:
 *   loading → HomeSkeleton
 *   error   → сообщение "Ошибка загрузки…"
 *   data    → HomeStatusBar + (опц) HeroCarousel + N HomeSection
 *
 * Edge cases:
 *   - heroBanners=[]                 → hero-слот не рендерится
 *   - sections=[]                    → просто status-bar
 *   - section.sectionType='VIDEO'    → пропускается в HomeSection
 *   - section.refData===null         → пропускается в HomeSection
 *   - user=null / credits=null (анон) → credits→0, plan→Free
 *
 * При mount сбрасывает скролл наверх (handoff §3).
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, skeletonData, loading, error } = useBootstrap();
  const authLoading = useAuthStore((state) => state.isLoading);
  const authUser = useAuthStore((state) => state.user);
  const consentRequired = useConsentStore((state) => state.consentRequired);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch {
        /* jsdom does not implement scrollTo — non-fatal in tests */
      }
    }
  }, []);

  useEffect(() => {
    if (!data || authLoading) return;

    const schedule =
      typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback.bind(window)
        : (callback) => window.setTimeout(callback, 120);

    const handle = schedule(() => {
      if (authUser && !consentRequired) {
        queryClient.prefetchInfiniteQuery({
          queryKey: queryKeys.gallery('all'),
          queryFn: ({ pageParam = 0, signal }) =>
            fetchGalleryPage(
              { filter: 'all', limit: GALLERY_PAGE_SIZE, offset: pageParam },
              signal,
            ),
          initialPageParam: 0,
          getNextPageParam: (lastPage) => {
            const nextOffset = Number(lastPage?.offset || 0) + (lastPage?.items?.length || 0);
            return nextOffset < Number(lastPage?.total || 0) ? nextOffset : undefined;
          },
        });
      }
      queryClient.prefetchQuery({
        queryKey: queryKeys.catalog('video', 'all-video'),
        queryFn: ({ signal }) => fetchCatalog({ type: 'video', rubric: 'all-video' }, signal),
      });
    });

    return () => {
      if (typeof window === 'undefined') return;
      if (typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle);
      }
    };
  }, [authLoading, authUser, consentRequired, data]);

  if (loading) return <HomeSkeleton bootstrap={skeletonData} />;

  if (error) {
    return (
      <div
        className="min-h-screen bg-bg-app text-white flex items-center justify-center px-8 text-center"
        role="alert"
      >
        <p className="font-sans text-sm text-text-soft">
          {t('home.loadError')}
        </p>
      </div>
    );
  }

  const heroBanners = data?.heroBanners || [];
  const sections = data?.sections || [];

  const onSlideTap = (banner) => {
    if (banner?.targetType === 'preset_group' && banner?.targetId) {
      const group = findGroupInBootstrap(data, banner.targetId);
      if (group) {
        seedPresetGroup(group);
      }
      navigate(`/group/${banner.targetId}`, group ? { state: { group } } : undefined);
    } else if (banner?.targetType === 'video_preset' && banner?.targetId) {
      navigate(`/video-generate/${banner.targetId}`);
    } else if (banner?.ctaLink) {
      navigate(banner.ctaLink);
    }
  };

  return (
    <div className="min-h-screen bg-bg-app text-white">
      {heroBanners.length > 0 && (
        <div className="px-0">
          <HeroCarousel banners={heroBanners} onSlideTap={onSlideTap} />
        </div>
      )}

      {sections.length > 0 && (
        <div className="flex flex-col gap-gap-section px-body-x pt-gap-section pb-body-pb">
          {sections.map((s) => (
            <HomeSection key={s.id} section={s} />
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          hapticSelection();
          navigate('/search');
        }}
        className={[
          'fixed left-1/2 z-30 inline-flex -translate-x-1/2 items-center gap-[7.5px]',
          'rounded-home-20 px-[7.5px] py-[3.75px]',
          'bg-gradient-to-l from-glass-24-start to-glass-24-end',
          'font-sans font-medium text-[15px] leading-[17.61px] text-white',
          'backdrop-blur-glass-strong transition-transform active:scale-[0.97]',
        ].join(' ')}
        style={{
          bottom:
            'calc(var(--safe-bottom, 0px) + 96px + var(--search-bottom-offset, 0px))',
        }}
        aria-label={t('home.search', 'Поиск')}
      >
        <Search aria-hidden="true" size={22} strokeWidth={2.4} />
        <span>{t('home.search', 'Поиск')}</span>
      </button>
    </div>
  );
}
